import React, { useState } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronDown, ChevronRight, Copy, Clock, BarChart3, Eye, Code, FileText } from "lucide-react";
import { toast } from "sonner";
import { PatternPreviewResult, PatternPreviewMetrics } from "@/services/patternPreviewService";

interface PatternPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  filteredHtml: string;
  originalHtml: string;
  isFetching: boolean;
  fetchError: string | null;
}

export const PatternPreviewModal: React.FC<PatternPreviewModalProps> = ({
  isOpen,
  onClose,
  filteredHtml,
  originalHtml,
  isFetching,
  fetchError
}) => {
  const [activeTab, setActiveTab] = useState<"filtered-raw" | "filtered-preview" | "original-raw">("filtered-raw");

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Content copied to clipboard");
    }).catch(() => {
      toast.error("Failed to copy content");
    });
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const getMetricsDisplay = (metrics: PatternPreviewMetrics) => {
    const isExclusionPattern = filteredHtml.startsWith('EXCLUDE:');
    const metricLabel = isExclusionPattern ? 'Content Removed' : 'Content Extracted';
    
    return {
      label: metricLabel,
      value: `${metrics.reductionPercentage.toFixed(1)}%`,
      color: isExclusionPattern ? 'text-red-600' : 'text-green-600'
    };
  };

  return (
    <BigFormModal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Pattern Preview"
      hideActions={true}
    >
      <div className="space-y-6">
        {/* Loading State */}
        {isFetching && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Applying pattern to URL...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {fetchError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800 mb-2">
              <Eye className="w-5 h-5" />
              <span className="font-semibold">Preview Failed</span>
            </div>
            <p className="text-red-700">{fetchError}</p>
          </div>
        )}

        {/* Success State - Preview Results */}
        {!isFetching && !fetchError && (
          <div className="space-y-4">
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "filtered-raw" | "filtered-preview" | "original-raw")}>
              <TabsList className="mb-4">
                <TabsTrigger value="filtered-raw" className="flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Filtered raw
                </TabsTrigger>
                <TabsTrigger value="filtered-preview" className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Filtered preview
                </TabsTrigger>
                <TabsTrigger value="original-raw" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Original raw
                </TabsTrigger>
              </TabsList>

              {/* Filtered Raw Tab */}
              <TabsContent value="filtered-raw" className="border rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
                  <span className="font-medium">Filtered HTML (Raw)</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(filteredHtml)}
                    className="h-8"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="p-4">
                  <pre className="whitespace-pre-wrap break-all text-sm font-mono bg-gray-50 p-4 rounded">
                    {filteredHtml || <span className="text-gray-500 italic">No content to display</span>}
                  </pre>
                </div>
              </TabsContent>

              {/* Filtered Preview Tab */}
              <TabsContent value="filtered-preview" className="border rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
                  <span className="font-medium">Filtered HTML (Preview)</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(filteredHtml)}
                    className="h-8"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="p-4">
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: filteredHtml || 'No content to display' }}
                  />
                </div>
              </TabsContent>

              {/* Original Raw Tab */}
              <TabsContent value="original-raw" className="border rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
                  <span className="font-medium">Original HTML (Raw)</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(originalHtml)}
                    className="h-8"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="p-4">
                  <pre className="whitespace-pre-wrap break-all text-sm font-mono bg-gray-50 p-4 rounded">
                    {originalHtml || <span className="text-gray-500 italic">No content to display</span>}
                  </pre>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </BigFormModal>
  );
}; 